import React, { useState, useEffect } from "react";
import TitlePage from "../../Shared/TitlePage";
import { makeStyles } from "@material-ui/core/styles";
import { dataService } from "../../../Services/data/data.service";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TableContainer, Table, Typography, Box, IconButton, TableHead, TableBody, TableRow, TableCell, Tooltip } from "@mui/material";
import { Plus, Pencil, BadgeCheck, BadgeMinus, Infinity, Users, Factory, EyeOff } from "lucide-react";
import UserCrudDialog from "./UserCrudDialog";
import CompanyCrudDialog from "./CompanyCrudDialog";

import { Roles } from "../../../Config/constants";
import UserEditDialog from "./UserEditDialog";
import { Spin } from "react-cssfx-loading";
import UserLimitReachedDialog from "../../Shared/Dialogs/UserLimitReachedDialog";
import EmptyStateCard from "../../Shared/EmptyStateCard";
import Chip from "../../Shared/ui/Chip";

toast.configure();

export default function CompanySettingsPage(props) {
  const classes = useStyles();
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [userCompanies, setUserCompanies] = useState(null);
  const [roles, setRoles] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [crudDialogOpen, setCrudDialogOpen] = useState(false);
  const [companyCrudDialogOpen, setCompanyCrudDialogOpen] = useState(false);
  const [userLimitReachedDialogOpen, setUserLimitReachedDialogOpen] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);

  const [companyToEdit, setCompanyToEdit] = useState(null);

  const [validSubscription, setValidSubscription] = useState(false);
  const [generalPageError, setGeneralPageError] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [companyCount, setCompanyCount] = useState(0);
  const [userCount, setUserCount] = useState(0);

  useEffect(() => {
    //Promise is used so functions will happen together.
    //When all are finished --> setLoading false
    Promise.all([dataService.getAllRoles()])
      .then((results) => {
        var roleResults = results[0];
        if (roleResults) {
          setRoles(roleResults);
        }
      })
      .catch((error) => {
        toast.error(`A problem occured while fetching data!`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        console.log("Error: ", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.Login.userid]);

  // Fetching companies and users
  useEffect(() => {
    if (props.Login.subscriberUniqueId !== null) {
      setUserIsAdmin(true);

      // get subscription information
      dataService
        .getSubscriptionBySubscriberUniqueId(props.Login.subscriberUniqueId)
        .then((result) => {
          if (result.success) {
            setSubscription(result.subscription);
            setValidSubscription(true);
          } else {
            setGeneralPageError("You don't have a valid subscription. This means you are not able to create companies or users.");
            setValidSubscription(false);
          }
        })
        .catch((error) => {
          toast.error(`You don't have a valid subscription`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        });

      // get companies and users for admin
      dataService
        .getCompaniesAndUsersBySubscriberUniqueId(props.Login.subscriberUniqueId)
        .then((result) => {
          if (result) {
            let tempCompanyCount = 0;
            let tempUserCount = 0;
            let tempUsers = [];

            result.forEach((company) => {
              if (company.isActive) {
                tempCompanyCount++;
              }

              // Check if company is hidden from current user
              if (company.users.find((x) => x.id === props.Login.userid)) {
                company.isHidden = company.users.find((x) => x.id === props.Login.userid).companyHidden;
              } else {
                company.isHidden = false;
              }

              company.users.forEach((user) => {
                if (user.enabled) {
                  // if there is not user in tempUsers with the emailaddress, add the user to tempUsers
                  if (!tempUsers.find((x) => x.emailAddress === user.emailAddress)) {
                    tempUsers.push(user);
                    tempUserCount++;
                  }
                }
              });
            });

            setCompanyCount(tempCompanyCount);
            setUserCount(tempUserCount);
            setUserCompanies(result);
          }
        })
        .catch((error) => {
          toast.error(`A problem occured while fetching data!`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
          console.log("Error: ", error);
        });
    } else {
      setUserIsAdmin(false);

      dataService
        .getUserAndCompanyData(props.Login.userid)
        .then((result) => {
          if (result) {
            let tempCompanyCount = 0;
            let tempUserCount = 0;
            let tempUsers = [];

            result.forEach((company) => {
              if (company.isActive) {
                tempCompanyCount++;
                company.users.forEach((user) => {
                  if (user.enabled) {
                    // if there is not user in tempUsers with the emailaddress, add the user to tempUsers
                    if (!tempUsers.find((x) => x.emailAddress === user.emailAddress)) {
                      tempUsers.push(user);
                      tempUserCount++;
                    }
                  }
                });
              }
            });

            setCompanyCount(tempCompanyCount);
            setUserCount(tempUserCount);
            setUserCompanies(result);
          }
        })
        .catch((error) => {
          toast.error(`A problem occured while fetching data!`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
          console.log("Error: ", error);
        });
    }
  }, [props.Login]);

  const handleCreateCompany = () => {
    setCompanyCrudDialogOpen(true);
    setCompanyId(null);
  };
  const closeCompanyCrudDialog = () => {
    setCompanyToEdit(null);
    setCompanyCrudDialogOpen(false);
  };

  const handleCreateUser = (intCompanyId) => {
    // backend check if subscriber has reached user limit
    dataService
      .checkSubscriptionUserLimitByCompanyId(intCompanyId)
      .then((result) => {
        let userLimitReached = result;

        if (userLimitReached) {
          setUserLimitReachedDialogOpen(true);
        } else {
          setCrudDialogOpen(true);
          setCompanyId(intCompanyId);
        }
      })
      .catch((error) => {
        toast.error(`A problem occured while fetching data!`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        console.log("Error: ", error);
      });
  };

  const closeCrudDialog = () => {
    setCrudDialogOpen(false);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
  };

  const nothing = (intUserLimit) => {
    toast.error(`Can't create another user, you have reached the limit of ${intUserLimit} active users!`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
  };

  const handleEditCompany = (company) => {
    setCompanyToEdit(company);
    setCompanyCrudDialogOpen(true);
  };

  const handleEditUser = (intCompanyId, intUserId) => {
    try {
      var arrCompanies = JSON.parse(JSON.stringify(userCompanies));
      var objCompany = arrCompanies.find((x) => x.id === intCompanyId);
      var objUser = objCompany.users.find((x) => x.id === intUserId);
      setUserToEdit(objUser);
      setCompanyId(intCompanyId);
      setEditDialogOpen(true);
    } catch (error) {
      console.log("error ", error);
      toast.error(`Something went wrong!`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    }
  };

  const getSubscriptionCompanyLimit = () => {
    if (subscription.companyLimit) {
      return subscription.companyLimit;
    } else {
      return <Infinity className={classes.infinityIcon} />;
    }
  };

  const getSubscriptionUserLimit = () => {
    if (subscription.userLimit) {
      return subscription.userLimit;
    } else {
      return <Infinity className={classes.infinityIcon} />;
    }
  };

  const isCompanyLimitReached = () => {
    if (subscription && subscription.companyLimit) {
      return companyCount >= subscription.companyLimit;
    } else {
      return false;
    }
  };

  const isUserLimitReached = () => {
    if (subscription && subscription.userLimit) {
      return userCount >= subscription.userLimit;
    } else {
      return false;
    }
  };

  return (
    <React.Fragment>
      {loading && (
        <div className={classes.loadingChart}>
          <Spin width="100px" height="100px" color="#C3996B"></Spin>
        </div>
      )}
      <TitlePage Text="Company settings" backbutton={true} {...props} />
      <Box
        sx={{
          maxWidth: "90%",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          marginRight: "auto",
          marginLeft: "auto",
          marginTop: "120px",
        }}
      >
        <div className={classes.header}>
          <h1>Manage companies</h1>
          <div className={classes.header__right}>
            {userIsAdmin && !validSubscription && (
              <div className={classes.noSubscriptionChip}>
                <p>No active subscription</p>
              </div>
            )}
            {subscription && (
              <div className={classes.subscriptionInformationContainer}>
                <div className={classes.subscriptionCompanyCountChip}>
                  <Factory className={classes.chip__icons} />
                  <p className={classes.chip__text}>
                    {companyCount}/{getSubscriptionCompanyLimit()}
                  </p>
                </div>
                <div className={classes.subscriptionUserCountChip}>
                  <Users className={classes.chip__icons} />
                  <p className={classes.chip__text}>
                    {userCount}/{getSubscriptionUserLimit()}
                  </p>
                </div>
              </div>
            )}
            {userIsAdmin && validSubscription && (
              <Tooltip title={isCompanyLimitReached() ? "You have reached your company limit. Please contact our team if you want to upgrade your subscription" : ""} placement="top" arrow>
                <button className={classes.tableManagementContainer__primaryIconButton} disabled={isCompanyLimitReached()} onClick={() => handleCreateCompany()}>
                  <Plus className={classes.iconButton__icon} />
                  Create company
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        <Box>
          <div className={classes.container}>
            {userCompanies && userCompanies.length == 0 && <EmptyStateCard title="You are not in any companies yet" />}
            {userCompanies && userCompanies.length > 0 && (
              <Box sx={{ borderRadius: "8px", maxWidth: "100%", marginRight: "auto", marginLeft: "auto" }}>
                {userCompanies.map((company, companyIndex) => {
                  return (
                    <Box key={"companyBox_" + companyIndex} sx={{ width: "100%", marginBottom: "24px" }}>
                      <Box className={classes.companyHeader}>
                        <Typography variant="h5" className={classes.projectTitle}>
                          {company.name}
                        </Typography>
                        <div className={classes.companyHeader__end}>
                          {company.isHidden && <Chip startAdornment={<EyeOff />}>Hidden</Chip>}
                          {company.isActive ? <Chip variant="success">Active</Chip> : <Chip variant="danger">Inactive</Chip>}
                          <Chip>
                            <p>{company.currencyName}</p>
                            <p style={{ marginLeft: "4px" }}>{company.currencySymbol}</p>
                          </Chip>
                          {userIsAdmin && validSubscription && (
                            <button onClick={() => handleEditCompany(company)} className={classes.iconButton}>
                              <Pencil className={classes.iconButton__icon} />
                            </button>
                          )}
                          {/* <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', padding: '15px' }}>User limit: {company.users.filter(x => x.enabled).length}/{company.userLimit}</Typography> */}
                        </div>
                      </Box>
                      {/* <Box sx={{ width: '100%', display: 'flex' }}> */}
                      <TableContainer className={classes.paper}>
                        <Table size="sm" key={"table"}>
                          <TableHead className={classes.headStyle} key={"thead"}>
                            <TableRow className={classes.tableHeadRow} key={"trow"}>
                              <TableCell className={classes.tableHead} align="center" sx={{ width: "3%" }}>
                                <span className={classes.titleSearchCenter}>
                                  <div className={classes.icons}>#</div>
                                </span>
                              </TableCell>
                              <TableCell className={classes.tableHead} sx={{ width: "28%" }}>
                                <span className={classes.titleSearch}>
                                  <div className={classes.icons}>Name</div>
                                </span>
                              </TableCell>
                              <TableCell className={classes.tableHead} sx={{ width: "28%" }}>
                                <span className={classes.titleSearch}>
                                  <div className={classes.icons}>Email</div>
                                </span>
                              </TableCell>
                              <TableCell className={classes.tableHead} align="center" sx={{ width: "10%" }}>
                                <span className={classes.titleSearchCenter}>
                                  <div className={classes.icons}>Active</div>
                                </span>
                              </TableCell>
                              <TableCell className={classes.tableHead} align="center" sx={{ border: "none !important" }}>
                                <IconButton disabled={!company.isActive || (userIsAdmin && !validSubscription)} style={{ borderRadius: 0 }} onClick={() => handleCreateUser(company.id)}>
                                  <Plus></Plus>
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody key={"tbody"}>
                            {[...company.users]
                              .sort((a, b) => a.firstname.localeCompare(b.firstname))
                              .map((row, rowIndex) => (
                                <TableRow className={classes.tableBodyRow} key={company.id + "_userRow_" + rowIndex}>
                                  <TableCell className={classes.tableCell} align="center" sx={{ width: "3%" }}>
                                    {rowIndex + 1}
                                  </TableCell>
                                  <TableCell className={classes.tableCell} sx={{ width: "42%", textAlign: "left", paddingLeft: "10px" }}>
                                    {row.firstname} {row.name}
                                  </TableCell>
                                  <TableCell className={classes.tableCell} sx={{ width: "42%", textAlign: "left", paddingLeft: "10px" }}>
                                    {row.emailAddress}
                                  </TableCell>
                                  <TableCell className={classes.tableCell} align="center" sx={{ width: "10%" }}>
                                    {row.enabled ? <BadgeCheck style={{ color: "green" }}></BadgeCheck> : <BadgeMinus style={{ color: "red" }}></BadgeMinus>}
                                  </TableCell>
                                  <TableCell className={classes.tableCell} align="center" sx={{ border: "none !important", padding: "0px !important" }}>
                                    <IconButton onClick={() => handleEditUser(company.id, row.id)} style={{ borderRadius: 0 }}>
                                      <Pencil></Pencil>
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })}
              </Box>
            )}
          </div>
        </Box>
      </Box>
      {companyCrudDialogOpen && (
        <CompanyCrudDialog
          dialogOpen={companyCrudDialogOpen}
          closeDialog={closeCompanyCrudDialog}
          roles={roles.filter((x) => x.id !== Roles.ADMIN)}
          companyId={companyId}
          companyToEdit={companyToEdit}
          userId={props.Login.userid}
          subscriberUniqueId={props.Login.subscriberUniqueId}
          {...props}
        ></CompanyCrudDialog>
      )}

      {crudDialogOpen && (
        <UserCrudDialog dialogOpen={crudDialogOpen} closeDialog={closeCrudDialog} roles={roles.filter((x) => x.id !== Roles.ADMIN)} companyId={companyId} userId={props.Login.userid}></UserCrudDialog>
      )}
      {editDialogOpen && (
        <UserEditDialog
          dialogOpen={editDialogOpen}
          closeDialog={closeEditDialog}
          roles={roles.filter((x) => x.id !== Roles.ADMIN)}
          companyId={companyId}
          userToEdit={userToEdit}
          userId={props.Login.userid}
          userCompanies={userCompanies}
        ></UserEditDialog>
      )}
      <UserLimitReachedDialog dialogOpen={userLimitReachedDialogOpen} closeDialog={() => setUserLimitReachedDialogOpen(false)} />
    </React.Fragment>
  );
}

const useStyles = makeStyles({
  header: {
    width: "100%",
    height: "40px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    marginBottom: "16px",
  },

  header__right: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
  },

  subscriptionInformationContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  subscriptionCompanyCountChip: {
    height: "24px",
    padding: "4px 8px 4px 6px",

    display: "flex",
    alignItems: "center",
    gap: "4px",

    border: "1px solid #DCDCDC",
    borderRadius: "12px",
    backgroundColor: "#EDEDED",
  },

  subscriptionUserCountChip: {
    height: "24px",
    padding: "4px 8px 4px 6px",

    display: "flex",
    alignItems: "center",
    gap: "4px",

    border: "1px solid #DCDCDC",
    borderRadius: "12px",
    backgroundColor: "#EDEDED",
  },

  noSubscriptionChip: {
    height: "28px",
    padding: "4px 8px",

    display: "flex",
    alignItems: "center",

    color: "#FDF3F3",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",

    backgroundColor: "#D84949",

    border: "1px solid #FBE5E5",
    borderRadius: "14px",
  },

  chip__icons: {
    width: "16px",
    height: "16px",
    color: "#0C0E29",
  },

  chip__text: {
    display: "flex",
    alignItems: "center",
    gap: "2px",

    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
  },

  loadingChart: {
    height: "100vh",
    width: "100%",
    top: "0px",
    zIndex: "999",
    position: "fixed",
    borderRadius: "8px",
    backgroundColor: "rgba(156,156,156,0.5)",
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
  },

  companyHeader: {
    width: "100%",
    backgroundColor: "#C3996B",
    color: "white",
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
    display: "flex",
    justifyContent: "space-between",

    padding: "16px",
  },

  companyHeader__end: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  chip: {
    height: "24px",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    color: "#7C7C7C",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",

    backgroundColor: "#EDEDED",

    borderRadius: "12px",
    padding: "4px 12px",
  },

  chip__success: {
    height: "24px",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    color: "#3AB037",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",

    backgroundColor: "#F3FBF2",

    borderRadius: "12px",
    padding: "4px 12px",
  },

  chip__danger: {
    height: "24px",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    color: "#C73B3B",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",

    backgroundColor: "#FDF3F3",

    borderRadius: "12px",
    padding: "4px 12px",
  },

  iconButton: {
    height: "32px",
    width: "32px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "0C0E29",
    backgroundColor: "#F8F8F8",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#EDEDED",
      cursor: "pointer",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    display: "block",
  },

  projectTitle: {
    padding: "0px",
  },

  tableHead: {
    height: "42px",
    borderRight: "2px solid #e6ecf0",
    padding: "0px !important",
  },

  tableCell: {
    paddingTop: "0px !important",
    paddingBottom: "0px !important",
  },
  paper: {
    width: "100%",
    background: "white",
  },
  titleSearch: {
    position: "relative",
    display: "flex",
    paddingLeft: "10px",
    // justifyContent: 'center',
    alignItems: "center",
  },
  titleSearchCenter: {
    position: "relative",
    display: "flex",
    // paddingLeft: '10px',
    justifyContent: "center",
    alignItems: "center",
  },
  headStyle: {
    backgroundColor: "#e0dce4",
  },
  tableBodyRow: {
    justifyContent: "center",
    textAlign: "center",
  },

  tableManagementContainer__primaryIconButton: {
    height: "32px",
    padding: "0 12px",
    paddingLeft: "6px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "#C3996B",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },

    "&:disabled": {
      backgroundColor: "#DCDCDC",
      color: "#BDBDBD",
      cursor: "not-allowed",

      "&:hover": {
        backgroundColor: "#DCDCDC",
        color: "#BDBDBD",
        cursor: "not-allowed",
      },
    },
  },
  actionButtons: {
    padding: "0px !important",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
  },
  tableFooter: {
    width: "100%",
  },
  footer: {
    display: "flex",
  },
  icons: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  infinityIcon: {
    width: "16px",
    height: "16px",
    color: "#0C0E29",
  },
});
